import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, HostListener, NgZone, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, filter, firstValueFrom, take, takeUntil } from 'rxjs';

import { ApiService } from '@core/api/api.service';
import { AuthService } from '@core/auth/auth.service';
import {
  BasketNeed,
  BasketNeedWrite,
  BasketSummary,
  BranchOption,
  CityOption,
  ProductBranchPrice,
  ProductSearchItem,
  ShoppingBasket
} from '@core/models/models';
import { LocationSelectorModalComponent } from '@shared/components/location-selector-modal/location-selector-modal.component';
import { SavedFieldDirective } from '@shared/directives/saved-field.directive';
import { LocalProfileService } from '@shared/services/local-profile.service';
import { GroupShareModalComponent } from '@features/shopping/components/group-share-modal/group-share-modal.component';
import { ProductSearchComponent } from '@features/shopping/components/product-search/product-search.component';
import { KgQuantityComponent } from '@shared/components/kg-quantity/kg-quantity.component';
import { cartItemCount, lineTotal } from '@core/weight-quantity';

import { matchingOpenLines, normalizeListText } from './list-label-match';

type PhonePanel = 'note' | 'search' | 'cart';

interface AddPicker {
  kind: 'add';
  product: ProductSearchItem;
  quantity: number;
  weightPhotoId?: number;
}

interface RetargetPicker {
  kind: 'retarget';
  need: BasketNeed;
}

@Component({
  selector: 'app-store-list',
  standalone: true,
  imports: [CommonModule, FormsModule, ProductSearchComponent, LocationSelectorModalComponent, GroupShareModalComponent, SavedFieldDirective, KgQuantityComponent],
  templateUrl: './store-list.component.html',
  styleUrl: './store-list.component.css'
})
export class StoreListComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly api = inject(ApiService);
  private readonly auth = inject(AuthService);
  private readonly localProfile = inject(LocalProfileService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private processingJoinToken = false;
  private promptedShareLogin = false;
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly zone = inject(NgZone);
  private readonly destroy$ = new Subject<void>();
  private readonly enterTimers = new Map<number, ReturnType<typeof setTimeout>>();
  private searchFocusTimer: ReturnType<typeof setTimeout> | null = null;
  private releasePhoneTabOffset: (() => void) | null = null;
  private panelResize: ResizeObserver | null = null;
  private touchStartX = 0;
  private touchStartY = 0;
  private touchStartTime = 0;
  private touchAxis: 'x' | 'y' | null = null;
  private alive = true;

  @ViewChild(ProductSearchComponent) private productSearch?: ProductSearchComponent;
  @ViewChild('notePanel') private notePanel?: ElementRef<HTMLElement>;
  @ViewChild('searchPanel') private searchPanel?: ElementRef<HTMLElement>;
  @ViewChild('cartPanel') private cartPanel?: ElementRef<HTMLElement>;
  @ViewChild('listNameInput') private listNameInput?: ElementRef<HTMLInputElement>;

  cities: CityOption[] = [];
  selectedCity = '';
  citiesLoading = false;
  branches: BranchOption[] = [];
  selectedBranchId: number | null = null;
  branchesLoading = false;
  locationSettingsOpen = false;

  basket: ShoppingBasket | null = null;
  basketLoading = false;
  shareGroupOpen = false;
  sharePreparing = false;
  phonePanel: PhonePanel = 'note';
  panelDragging = false;
  panelDrag = 0;
  private panelHeight = 0;
  noteText = '';
  noteBusy = false;
  errorMessage: string | null = null;
  statusMessage: string | null = null;

  readonly addingProductIds = new Set<number>();
  readonly busyNeedIds = new Set<number>();
  readonly enteringNeedIds = new Set<number>();
  listPulse = false;
  readonly catalogPrices = new Map<number, ProductBranchPrice>();
  readonly productIdsWithImages = new Set<number>();
  readonly brokenImageIds = new Set<number>();

  cameraAvailable = false;
  imagePopupProductId: number | null = null;
  picker: AddPicker | RetargetPicker | null = null;
  pickerBusy = false;

  ngOnInit(): void {
    this.rememberIncomingShare();
    this.bindPhoneTabOffset();
    this.refreshCameraAvailable();
    this.loadCities();
    this.auth.sessionChecked$
      .pipe(filter((checked) => checked), take(1), takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.isStoreListShare()) {
          if (!this.promptedShareLogin) {
            this.promptedShareLogin = true;
            if (!this.auth.isLoggedIn) this.auth.openLoginModal();
            else this.acceptStoreListShare();
          }
          return;
        }
        this.loadStoredBasket();
      });
    this.auth.currentUser$.pipe(takeUntil(this.destroy$)).subscribe((user) => {
      if (!user) {
        if (this.localProfile.getStoreListBasketId() == null) this.basket = null;
        return;
      }
      if (this.isStoreListShare()) {
        this.acceptStoreListShare();
        return;
      }
      if (this.localProfile.getStoreListBasketId() == null && !this.basket && !this.basketLoading) {
        this.loadStoredBasket();
      }
    });
  }

  ngAfterViewInit(): void {
    const panels = [this.notePanel, this.searchPanel, this.cartPanel]
      .map((ref) => ref?.nativeElement)
      .filter((element): element is HTMLElement => !!element);
    if (typeof ResizeObserver !== 'undefined' && panels.length > 0) {
      this.panelResize = new ResizeObserver(() => {
        this.zone.run(() => this.syncPanelHeight());
      });
      for (const panel of panels) this.panelResize.observe(panel);
    }
    this.syncPageFill();
    this.syncPanelHeight();
  }

  ngOnDestroy(): void {
    this.releasePhoneTabOffset?.();
    this.releasePhoneTabOffset = null;
    this.panelResize?.disconnect();
    this.panelResize = null;
    this.alive = false;
    for (const timer of this.enterTimers.values()) clearTimeout(timer);
    this.enterTimers.clear();
    if (this.searchFocusTimer != null) clearTimeout(this.searchFocusTimer);
    this.destroy$.next();
    this.destroy$.complete();
  }

  listCloseBusy = false;
  renamingList = false;
  renameText = '';
  savingListName = false;
  listNameSaved = 0;
  private renameDismissed = false;

  get listTitle(): string {
    return this.basket?.name?.trim() || 'רשימה לסופר';
  }

  startRename(): void {
    if (this.isBasketClosed || this.renamingList || this.savingListName) return;
    this.renameDismissed = false;
    this.renameText = this.listTitle;
    this.renamingList = true;
    this.focusListNameInput();
  }

  cancelRename(): void {
    this.renameDismissed = true;
    this.renamingList = false;
    this.renameText = '';
  }

  async saveListName(): Promise<void> {
    if (!this.renamingList || this.renameDismissed || this.savingListName) return;
    const trimmed = this.renameText.trim();
    const current = this.basket?.name?.trim() || '';
    this.renamingList = false;
    if (!trimmed || trimmed === current || (!this.basket && trimmed === 'רשימה לסופר')) return;

    this.savingListName = true;
    this.errorMessage = null;
    try {
      const basket = await this.ensureBasket();
      if (trimmed === (basket.name?.trim() || '')) return;
      const updated = await firstValueFrom(this.api.renameShoppingBasket(basket.id, trimmed));
      this.basket = { ...(this.basket ?? basket), name: updated.name };
      this.listNameSaved++;
    } catch (error) {
      this.errorMessage = this.readError(error);
      this.renameText = trimmed;
      this.renameDismissed = false;
      this.renamingList = true;
      this.focusListNameInput();
    } finally {
      this.savingListName = false;
    }
  }

  private focusListNameInput(): void {
    setTimeout(() => {
      if (!this.alive || !this.renamingList) return;
      const input = this.listNameInput?.nativeElement;
      if (!input) return;
      input.focus();
      input.select();
    });
  }

  get selectedBranchLabel(): string {
    const branch = this.branches.find((item) => item.id === this.selectedBranchId);
    return branch ? `${branch.chainName} · ${branch.branchName}` : '';
  }

  get hasSelectedLocation(): boolean {
    return !!(this.selectedCity && this.selectedBranchId != null);
  }

  get isBasketClosed(): boolean {
    return !!(this.basket?.isClosed || this.basket?.closedAt);
  }

  toggleListClosed(): void {
    if (!this.basket || this.listCloseBusy) return;
    const basketId = this.basket.id;
    this.listCloseBusy = true;
    const request = this.isBasketClosed
      ? this.auth.openBasket(basketId)
      : this.auth.closeBasket(basketId);
    request.subscribe({
      next: (basket) => {
        this.basket = basket;
        this.listCloseBusy = false;
      },
      error: () => {
        this.listCloseBusy = false;
        this.errorMessage = 'אירעה שגיאה בעדכון הרשימה.';
      }
    });
  }

  get openNeeds(): BasketNeed[] {
    return (this.basket?.needs ?? []).filter((need) => need.lockedProductId == null);
  }

  get cartNeeds(): BasketNeed[] {
    return (this.basket?.needs ?? []).filter((need) => need.lockedProductId != null);
  }

  private readonly weightPhotoByNeed = new Map<number, number>();
  readonly estimatingNeedIds = new Set<number>();

  get cartCount(): number {
    return cartItemCount(this.cartNeeds);
  }

  cartLineTotal(need: BasketNeed): number | null {
    const pay = this.payPrice(need);
    if (pay == null) return null;
    return lineTotal(pay, need.quantity, !!need.soldByWeight, this.catalogPrice(need)?.unitOfMeasure);
  }

  get cartTotal(): number | null {
    let total = 0;
    let any = false;
    for (const need of this.cartNeeds) {
      const line = this.cartLineTotal(need);
      if (line == null) continue;
      total += line;
      any = true;
    }
    return any ? total : null;
  }

  openLocationSettings(): void {
    this.locationSettingsOpen = true;
  }

  closeLocationSettings(): void {
    this.locationSettingsOpen = false;
  }

  async openShare(): Promise<void> {
    if (this.sharePreparing) return;
    if (!this.auth.isLoggedIn) {
      this.auth.openLoginModal();
      return;
    }
    this.sharePreparing = true;
    this.errorMessage = null;
    try {
      this.basket = await this.ensureOwnedBasket();
      this.shareGroupOpen = true;
    } catch (error) {
      this.errorMessage = this.readError(error);
    } finally {
      this.sharePreparing = false;
    }
  }

  closeShare(): void {
    this.shareGroupOpen = false;
  }

  onBasketShared(updated: ShoppingBasket): void {
    this.basket = updated;
    this.rememberClaimToken(updated);
  }

  onCityChange(city: string): void {
    this.selectedCity = city;
    this.localProfile.setSelectedCity(city);
    this.selectedBranchId = null;
    this.localProfile.setSelectedBranchId(null);
    this.branches = [];
    this.catalogPrices.clear();
    if (city) this.loadBranches(city);
  }

  onBranchChange(branchId: number | null): void {
    this.selectedBranchId = branchId == null ? null : Number(branchId);
    this.localProfile.setSelectedBranchId(this.selectedBranchId);
    this.catalogPrices.clear();
    if (this.auth.isLoggedIn) {
      this.auth.updateProfile({
        preferredCity: this.selectedCity || null,
        preferredBranchId: this.selectedBranchId
      }).catch(() => {});
    }
    if (this.basket && this.selectedBranchId != null) {
      const basketId = this.basket.id;
      this.api.setBasketBranch(basketId, this.selectedBranchId).subscribe({
        next: (updated) => {
          if (this.basket?.id === updated.id) {
            this.basket = { ...this.basket, branchId: updated.branchId };
          }
        },
        error: () => {}
      });
    }
    this.refreshCatalogPrices();
  }

  searchFromList(need: BasketNeed): void {
    const text = this.lineText(need);
    if (!text) return;
    if (this.phoneTabsVisible()) this.showPanel('search');
    this.productSearch?.onProductSearchInput(text);
    if (this.searchFocusTimer != null) clearTimeout(this.searchFocusTimer);
    this.searchFocusTimer = setTimeout(() => {
      this.searchFocusTimer = null;
      if (this.alive) this.productSearch?.focusSearch();
    });
  }

  async addNoteLines(): Promise<void> {
    const parts = this.noteText
      .split(/[,،\n]+/)
      .map((part) => part.trim())
      .filter((part) => part.length > 0);
    if (parts.length === 0 || this.noteBusy || this.isBasketClosed) return;

    this.noteBusy = true;
    this.clearMessages();
    let added = 0;
    let skipped = 0;
    try {
      for (const part of parts) {
        if (this.openNeeds.some((need) => normalizeListText(this.lineText(need)) === normalizeListText(part))) {
          skipped += 1;
          continue;
        }
        const basket = await this.ensureBasket();
        const created = await firstValueFrom(this.api.createBasketNeed(basket.id, {
          requestedText: part,
          quantity: 1,
          policy: 'best_value',
          lockedProductId: null,
          unitSize: null,
          unitUom: null,
          packCount: null,
          approvedProductIds: [],
          listLabel: part
        }));
        this.patchNeed(created, true);
        added += 1;
      }
      if (added > 0) this.noteText = '';
      if (skipped > 0 && added === 0) this.statusMessage = 'הפריט כבר ברשימה.';
    } catch (error) {
      this.errorMessage = this.readError(error);
    } finally {
      this.noteBusy = false;
    }
  }

  async addProduct(product: ProductSearchItem, quantity = 1, weightPhotoId?: number): Promise<void> {
    if (this.picker || this.isBasketClosed) {
      if (this.isBasketClosed) this.errorMessage = 'הרשימה סגורה. יש לפתוח אותה מחדש כדי להוסיף מוצרים.';
      return;
    }
    if (!this.selectedBranchId) {
      this.errorMessage = 'יש לבחור סניף פיזי לפני הוספת מוצר.';
      return;
    }
    if (!Number.isFinite(quantity) || quantity <= 0) return;

    const existing = this.cartNeeds.find((need) => need.lockedProductId === product.id);
    if (existing) {
      await this.changeQuantity(existing, existing.quantity + quantity, weightPhotoId);
      this.rememberPrice(product);
      this.statusMessage = 'הכמות עודכנה ברשימה.';
      return;
    }

    const matches = matchingOpenLines(this.productNames(product), this.openNeeds.map((need) => ({
      id: need.id,
      text: this.lineText(need)
    })));
    if (this.openNeeds.length === 0) {
      await this.createExtra(product, quantity, weightPhotoId);
      return;
    }
    if (matches.length === 1) {
      const line = this.openNeeds.find((need) => need.id === matches[0].id);
      if (!line) return;
      try {
        await this.fulfillLine(line, product, quantity, weightPhotoId);
      } catch (error) {
        this.errorMessage = this.readError(error);
      }
      return;
    }

    this.addingProductIds.add(product.id);
    this.picker = { kind: 'add', product, quantity, weightPhotoId };
  }

  async chooseOpenLine(line: BasketNeed): Promise<void> {
    const picker = this.picker;
    if (!picker || this.pickerBusy) return;
    this.pickerBusy = true;
    this.clearMessages();
    try {
      if (picker.kind === 'add') {
        await this.fulfillLine(line, picker.product, picker.quantity, picker.weightPhotoId);
      } else {
        await this.moveBadge(picker.need, line);
      }
      this.closePicker();
    } catch (error) {
      this.errorMessage = this.readError(error);
    } finally {
      this.pickerBusy = false;
    }
  }

  async chooseNotFromList(): Promise<void> {
    const picker = this.picker;
    if (!picker || this.pickerBusy) return;
    this.pickerBusy = true;
    this.clearMessages();
    try {
      if (picker.kind === 'add') {
        await this.createExtra(picker.product, picker.quantity);
      } else if (picker.need.listLabel) {
        await this.clearBadge(picker.need);
      }
      this.closePicker();
    } catch (error) {
      this.errorMessage = this.readError(error);
    } finally {
      this.pickerBusy = false;
    }
  }

  cancelPicker(): void {
    if (this.pickerBusy) return;
    this.closePicker();
  }

  openBadgePicker(need: BasketNeed): void {
    if (this.isBasketClosed || this.picker || !need.listLabel) return;
    this.picker = { kind: 'retarget', need };
  }

  async removeOpenNeed(need: BasketNeed): Promise<void> {
    if (!this.basket || this.isBasketClosed || this.busyNeedIds.has(need.id)) return;
    this.blurRemoveFocus();
    this.busyNeedIds.add(need.id);
    this.clearMessages();
    try {
      await firstValueFrom(this.api.deleteBasketNeed(this.basket.id, need.id));
      this.basket = {
        ...this.basket,
        needs: this.basket.needs.filter((item) => item.id !== need.id),
        needCount: Math.max(0, this.basket.needCount - 1)
      };
    } catch (error) {
      this.errorMessage = this.readError(error);
    } finally {
      this.busyNeedIds.delete(need.id);
    }
  }

  async removeCartNeed(need: BasketNeed): Promise<void> {
    if (!this.basket || this.isBasketClosed || this.busyNeedIds.has(need.id)) return;
    this.blurRemoveFocus();
    const label = need.listLabel?.trim() || '';
    this.busyNeedIds.add(need.id);
    this.clearMessages();
    try {
      await firstValueFrom(this.api.deleteBasketNeed(this.basket.id, need.id));
      this.basket = {
        ...this.basket,
        needs: this.basket.needs.filter((item) => item.id !== need.id),
        needCount: Math.max(0, this.basket.needCount - 1)
      };
      if (label && !this.hasOpenText(label)) {
        await this.createOpenLine(label);
      }
    } catch (error) {
      this.errorMessage = this.readError(error);
    } finally {
      this.busyNeedIds.delete(need.id);
    }
  }

  async adjustSearchQuantity(
    product: ProductSearchItem,
    quantity: number,
    weightPhotoId?: number
  ): Promise<void> {
    const existing = this.cartNeeds.find((need) => need.lockedProductId === product.id);
    if (!existing) return;
    await this.changeQuantity(existing, quantity, weightPhotoId);
  }

  async changeQuantity(need: BasketNeed, quantity: number, weightPhotoId?: number): Promise<void> {
    if (!this.basket || this.isBasketClosed || quantity <= 0 || this.busyNeedIds.has(need.id)) return;
    const photoId = weightPhotoId ?? this.weightPhotoByNeed.get(need.id);
    if (photoId) this.weightPhotoByNeed.set(need.id, photoId);
    this.busyNeedIds.add(need.id);
    this.clearMessages();
    try {
      const updated = await firstValueFrom(this.api.updateBasketNeed(
        this.basket.id,
        need.id,
        { ...this.toWrite(need), quantity, weightPhotoId: photoId }
      ));
      this.patchNeed(updated, false);
    } catch (error) {
      this.errorMessage = this.readError(error);
    } finally {
      this.busyNeedIds.delete(need.id);
    }
  }

  async estimateCartWeight(need: BasketNeed, frameJpeg: string): Promise<void> {
    if (need.lockedProductId == null || this.estimatingNeedIds.has(need.id)) return;
    this.estimatingNeedIds.add(need.id);
    try {
      const result = await firstValueFrom(
        this.api.estimateBagWeight(need.lockedProductId, need.requestedText, frameJpeg)
      );
      if (!result.readable || result.kilograms == null) {
        this.errorMessage = 'לא הצלחנו להעריך את משקל השקית. נסו שוב או הקלידו את המשקל.';
        return;
      }
      this.weightPhotoByNeed.set(need.id, result.photoId);
      await this.changeQuantity(need, result.kilograms, result.photoId);
    } catch (error) {
      this.errorMessage = this.readError(error);
    } finally {
      this.estimatingNeedIds.delete(need.id);
    }
  }

  lineText(need: BasketNeed): string {
    return (need.listLabel || need.requestedText).trim();
  }

  payPrice(need: BasketNeed): number | null {
    const price = need.lockedProductId == null ? undefined : this.catalogPrices.get(need.lockedProductId);
    if (!price) return null;
    return price.promoPrice != null ? price.promoPrice : price.price;
  }

  catalogPrice(need: BasketNeed): ProductBranchPrice | undefined {
    if (need.lockedProductId == null) return undefined;
    return this.catalogPrices.get(need.lockedProductId);
  }

  formatMoney(value: number | null, currency = 'ILS'): string {
    if (value == null) return 'אין מחיר';
    try {
      return new Intl.NumberFormat('he-IL', {
        style: 'currency',
        currency,
        maximumFractionDigits: 2
      }).format(value);
    } catch {
      return `${value.toFixed(2)} ${currency}`;
    }
  }

  formatQuantity(quantity: number): string {
    return Number.isInteger(quantity)
      ? String(quantity)
      : quantity.toLocaleString('he-IL', { maximumFractionDigits: 2 });
  }

  productImageUrl(productId: number): string {
    return this.api.productImageUrl(productId);
  }

  hasProductPhoto(productId: number | null): boolean {
    return productId != null && this.productIdsWithImages.has(productId) && !this.brokenImageIds.has(productId);
  }

  markImageBroken(productId: number): void {
    this.brokenImageIds.add(productId);
    if (this.imagePopupProductId === productId) this.imagePopupProductId = null;
  }

  @HostListener('document:keydown.escape')
  closeOnEscape(): void {
    if (this.picker && !this.pickerBusy) {
      this.closePicker();
      return;
    }
    if (this.imagePopupProductId != null) this.imagePopupProductId = null;
    else if (this.locationSettingsOpen) this.closeLocationSettings();
  }

  private async fulfillLine(
    line: BasketNeed,
    product: ProductSearchItem,
    quantity: number,
    weightPhotoId?: number
  ): Promise<void> {
    if (!this.basket) return;
    const label = this.lineText(line);
    this.addingProductIds.add(product.id);
    this.busyNeedIds.add(line.id);
    try {
      const updated = await firstValueFrom(this.api.updateBasketNeed(this.basket.id, line.id, {
        ...this.toWrite(line),
        requestedText: this.displayName(product),
        quantity,
        policy: 'exact',
        lockedProductId: product.id,
        listLabel: label,
        weightPhotoId
      }));
      this.patchNeed(updated, false);
      if (weightPhotoId) this.weightPhotoByNeed.set(updated.id, weightPhotoId);
      this.rememberPrice(product);
      this.statusMessage = null;
    } finally {
      this.addingProductIds.delete(product.id);
      this.busyNeedIds.delete(line.id);
    }
  }

  private async createExtra(
    product: ProductSearchItem,
    quantity: number,
    weightPhotoId?: number
  ): Promise<void> {
    this.addingProductIds.add(product.id);
    this.clearMessages();
    try {
      const basket = await this.ensureBasket();
      const created = await firstValueFrom(this.api.createBasketNeed(basket.id, {
        requestedText: this.displayName(product),
        quantity,
        policy: 'exact',
        lockedProductId: product.id,
        unitSize: null,
        unitUom: null,
        packCount: null,
        approvedProductIds: [],
        listLabel: null,
        weightPhotoId
      }));
      this.patchNeed(created, true);
      if (weightPhotoId) this.weightPhotoByNeed.set(created.id, weightPhotoId);
      this.rememberPrice(product);
      this.statusMessage = 'המוצר נוסף לסל.';
    } catch (error) {
      this.errorMessage = this.readError(error);
    } finally {
      this.addingProductIds.delete(product.id);
    }
  }

  private async moveBadge(cartNeed: BasketNeed, line: BasketNeed): Promise<void> {
    if (!this.basket) return;
    const previous = cartNeed.listLabel?.trim() || '';
    const nextLabel = this.lineText(line);
    const updated = await firstValueFrom(this.api.updateBasketNeed(this.basket.id, cartNeed.id, {
      ...this.toWrite(cartNeed),
      listLabel: nextLabel
    }));
    this.patchNeed(updated, false);
    await firstValueFrom(this.api.deleteBasketNeed(this.basket.id, line.id));
    this.basket = {
      ...this.basket,
      needs: this.basket.needs.filter((item) => item.id !== line.id)
    };
    if (previous && normalizeListText(previous) !== normalizeListText(nextLabel) && !this.hasOpenText(previous)) {
      await this.createOpenLine(previous);
    }
    this.statusMessage = null;
  }

  private async clearBadge(cartNeed: BasketNeed): Promise<void> {
    const label = cartNeed.listLabel?.trim() || '';
    if (!this.basket || !label) return;
    const updated = await firstValueFrom(this.api.updateBasketNeed(this.basket.id, cartNeed.id, {
      ...this.toWrite(cartNeed),
      listLabel: null
    }));
    this.patchNeed(updated, false);
    if (!this.hasOpenText(label)) await this.createOpenLine(label);
  }

  private async createOpenLine(text: string): Promise<void> {
    const basket = await this.ensureBasket();
    const created = await firstValueFrom(this.api.createBasketNeed(basket.id, {
      requestedText: text,
      quantity: 1,
      policy: 'best_value',
      lockedProductId: null,
      unitSize: null,
      unitUom: null,
      packCount: null,
      approvedProductIds: [],
      listLabel: text
    }));
    this.patchNeed(created, true);
  }

  private async ensureBasket(): Promise<ShoppingBasket> {
    if (this.basket) return this.basket;
    const basket = await firstValueFrom(this.api.createShoppingBasket(
      'רשימה לסופר',
      null,
      this.selectedBranchId,
      { makeCurrent: false, useDefaultGroup: false }
    ));
    this.localProfile.setStoreListBasketId(basket.id);
    this.rememberClaimToken(basket);
    this.basket = basket;
    return basket;
  }

  private async ensureOwnedBasket(): Promise<ShoppingBasket> {
    const basket = await this.ensureBasket();
    if (basket.userId != null) return basket;
    const claimed = await firstValueFrom(
      this.api.claimShoppingBasket(basket.id, this.localProfile.getStoreListBasketClaimToken())
    );
    this.localProfile.setStoreListBasketClaimToken(null);
    this.basket = claimed;
    return claimed;
  }

  private rememberClaimToken(basket: ShoppingBasket): void {
    if (basket.claimToken) {
      this.localProfile.setStoreListBasketClaimToken(basket.claimToken);
    } else if (basket.userId != null) {
      this.localProfile.setStoreListBasketClaimToken(null);
    }
  }

  private rememberIncomingShare(): void {
    const params = this.route.snapshot.queryParamMap;
    const joinToken = params.get('joinToken');
    const basketId = params.get('basketId');
    if (joinToken) this.writePending('commpare_pending_join_token', joinToken);
    if (basketId) this.writePending('commpare_pending_basket_id', basketId);
    if (params.get('page') === 'store-list') this.writePending('commpare_pending_share_page', 'store-list');
  }

  private isStoreListShare(): boolean {
    return this.route.snapshot.queryParamMap.get('page') === 'store-list' || this.pendingSharePage() === 'store-list';
  }

  private acceptStoreListShare(): void {
    const basketIdNum = Number(this.route.snapshot.queryParamMap.get('basketId') || this.pendingBasketId());
    const basketId = Number.isFinite(basketIdNum) ? basketIdNum : null;
    const token = this.route.snapshot.queryParamMap.get('joinToken') || this.pendingJoinToken();
    if (token) {
      if (this.processingJoinToken) return;
      this.processingJoinToken = true;
      this.auth.joinGroupByToken(token).subscribe({
        next: () => {
          this.processingJoinToken = false;
          this.openSharedList(basketId);
        },
        error: (err) => {
          this.processingJoinToken = false;
          this.clearPendingShare();
          this.stripShareQuery();
          this.statusMessage = err?.error?.error || 'קישור ההזמנה פג תוקף או שאינו תקין.';
        }
      });
      return;
    }
    this.openSharedList(basketId);
  }

  private openSharedList(basketId: number | null): void {
    if (basketId != null) this.localProfile.setStoreListBasketId(basketId);
    this.clearPendingShare();
    this.stripShareQuery();
    this.loadStoredBasket();
  }

  private stripShareQuery(): void {
    const current = { ...this.route.snapshot.queryParams };
    if (
      current['joinToken'] === undefined &&
      current['basketId'] === undefined &&
      current['page'] === undefined &&
      current['login'] === undefined
    ) {
      return;
    }
    delete current['joinToken'];
    delete current['basketId'];
    delete current['page'];
    delete current['login'];
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: current,
      queryParamsHandling: '',
      replaceUrl: true
    });
  }

  private pendingJoinToken(): string | null {
    return this.readPending('commpare_pending_join_token');
  }

  private pendingBasketId(): string | null {
    return this.readPending('commpare_pending_basket_id');
  }

  private pendingSharePage(): string | null {
    return this.readPending('commpare_pending_share_page') === 'store-list' ? 'store-list' : null;
  }

  private readPending(key: string): string | null {
    try {
      return sessionStorage.getItem(key) || localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private writePending(key: string, value: string): void {
    try {
      sessionStorage.setItem(key, value);
      localStorage.setItem(key, value);
    } catch {}
  }

  private clearPendingShare(): void {
    for (const key of ['commpare_pending_join_token', 'commpare_pending_basket_id', 'commpare_pending_share_page']) {
      try {
        sessionStorage.removeItem(key);
        localStorage.removeItem(key);
      } catch {}
    }
  }

  private loadStoredBasket(): void {
    const id = this.localProfile.getStoreListBasketId();
    if (id != null) {
      this.fetchBasket(id);
      return;
    }
    if (!this.auth.isLoggedIn || this.basketLoading) return;

    this.basketLoading = true;
    this.auth.getUserBaskets().subscribe({
      next: (baskets) => {
        const match = this.newestOpenList(baskets);
        if (!match) {
          this.basketLoading = false;
          return;
        }
        this.localProfile.setStoreListBasketId(match.id);
        this.fetchBasket(match.id);
      },
      error: () => {
        this.basketLoading = false;
      }
    });
  }

  private newestOpenList(baskets: BasketSummary[]): BasketSummary | undefined {
    return baskets
      .filter((basket) => !basket.closedAt)
      .sort((a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt))[0];
  }

  private fetchBasket(id: number): void {
    if (this.basket?.id === id) {
      this.basketLoading = false;
      return;
    }
    this.basketLoading = true;
    this.api.getShoppingBasket(id).subscribe({
      next: (basket) => {
        this.basket = basket;
        this.rememberClaimToken(basket);
        this.basketLoading = false;
        this.refreshCatalogPrices();
      },
      error: () => {
        this.basketLoading = false;
        this.basket = null;
        this.localProfile.clearStoreListBasketId();
      }
    });
  }

  private loadCities(): void {
    this.citiesLoading = true;
    this.api.listCities().subscribe({
      next: (response) => {
        this.cities = response.cities;
        const storedCity = this.localProfile.getSelectedCity();
        const userCity = this.auth.currentUser?.preferredCity;
        const cityToUse = (storedCity && this.cities.some((city) => city.value === storedCity))
          ? storedCity
          : (userCity && this.cities.some((city) => city.value === userCity))
            ? userCity
            : '';
        if (cityToUse) {
          this.selectedCity = cityToUse;
          this.localProfile.setSelectedCity(cityToUse);
          this.loadBranches(cityToUse);
        }
        this.citiesLoading = false;
      },
      error: (error) => {
        this.citiesLoading = false;
        this.errorMessage = this.readError(error);
      }
    });
  }

  private loadBranches(city: string): void {
    this.branchesLoading = true;
    this.api.listPhysicalBranches(city).subscribe({
      next: (response) => {
        if (this.selectedCity !== city) return;
        this.branches = response.branches;
        const storedBranchId = this.localProfile.getSelectedBranchId();
        const userBranchId = this.auth.currentUser?.preferredBranchId;
        const branchToUse = (storedBranchId && this.branches.some((branch) => branch.id === storedBranchId))
          ? storedBranchId
          : (userBranchId && this.branches.some((branch) => branch.id === userBranchId))
            ? userBranchId
            : null;
        this.selectedBranchId = branchToUse;
        this.localProfile.setSelectedBranchId(branchToUse);
        this.branchesLoading = false;
        this.refreshCatalogPrices();
      },
      error: (error) => {
        if (this.selectedCity !== city) return;
        this.branches = [];
        this.selectedBranchId = null;
        this.branchesLoading = false;
        this.errorMessage = this.readError(error);
      }
    });
  }

  private refreshCatalogPrices(): void {
    const branchId = this.selectedBranchId;
    const productIds = [...new Set(
      this.cartNeeds.map((need) => need.lockedProductId).filter((id): id is number => id != null)
    )];
    if (branchId == null || productIds.length === 0) return;
    this.api.listBranchProductPrices(branchId, productIds).subscribe({
      next: (response) => {
        if (this.selectedBranchId !== branchId) return;
        this.catalogPrices.clear();
        for (const item of response.items) {
          this.catalogPrices.set(item.productId, item);
          if (item.hasImage) this.productIdsWithImages.add(item.productId);
        }
      }
    });
  }

  private patchNeed(need: BasketNeed, added: boolean): void {
    if (!this.basket) {
      this.basket = null;
      return;
    }
    const exists = this.basket.needs.some((item) => item.id === need.id);
    const needs = exists
      ? this.basket.needs.map((item) => (item.id === need.id ? need : item))
      : [...this.basket.needs, need];
    this.basket = {
      ...this.basket,
      needs,
      needCount: added && !exists ? this.basket.needCount + 1 : needs.length
    };
    if (added && !exists && need.lockedProductId == null) this.markEntering(need.id);
  }

  private markEntering(needId: number): void {
    this.enteringNeedIds.add(needId);
    this.listPulse = false;
    const pending = this.enterTimers.get(needId);
    if (pending) clearTimeout(pending);
    this.enterTimers.set(needId, setTimeout(() => this.finishEnter(needId), 980));
    setTimeout(() => this.revealEntered(needId));
  }

  private revealEntered(needId: number): void {
    if (!this.alive) return;
    this.listPulse = this.enteringNeedIds.size > 0;
    if (this.phoneTabsVisible() && this.phonePanel !== 'note') return;
    const row = document.querySelector(`.note-list [data-need-id="${needId}"]`);
    if (!(row instanceof HTMLElement)) return;
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    row.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'nearest', inline: 'nearest' });
  }

  private blurRemoveFocus(): void {
    const active = document.activeElement;
    if (active instanceof HTMLElement && active.closest('.note-remove, .cart-remove')) active.blur();
  }

  private finishEnter(needId: number): void {
    if (!this.alive) return;
    this.enteringNeedIds.delete(needId);
    this.enterTimers.delete(needId);
    if (this.enteringNeedIds.size === 0) this.listPulse = false;
  }

  private hasOpenText(text: string): boolean {
    const normalized = normalizeListText(text);
    return this.openNeeds.some((need) => normalizeListText(this.lineText(need)) === normalized);
  }

  private toWrite(need: BasketNeed): BasketNeedWrite {
    return {
      requestedText: need.requestedText,
      quantity: need.quantity,
      policy: need.policy,
      lockedProductId: need.lockedProductId,
      unitSize: need.unitSize,
      unitUom: need.unitUom,
      packCount: need.packCount,
      approvedProductIds: [...need.approvedProductIds],
      listLabel: need.listLabel ?? null
    };
  }

  private productNames(product: ProductSearchItem): string[] {
    return [product.displayName, product.nameHe ?? '', product.nameRaw];
  }

  private displayName(product: ProductSearchItem): string {
    return product.displayName || product.nameHe || product.nameRaw;
  }

  private rememberPrice(product: ProductSearchItem): void {
    if (product.prices[0]) this.catalogPrices.set(product.id, product.prices[0]);
    if (product.hasImage) this.productIdsWithImages.add(product.id);
  }

  private closePicker(): void {
    if (this.picker?.kind === 'add') this.addingProductIds.delete(this.picker.product.id);
    this.picker = null;
  }

  private clearMessages(): void {
    this.errorMessage = null;
    this.statusMessage = null;
  }

  private readError(error: unknown): string {
    if (error && typeof error === 'object' && 'error' in error) {
      const responseBody = (error as { error?: unknown }).error;
      if (
        responseBody &&
        typeof responseBody === 'object' &&
        'error' in responseBody &&
        typeof (responseBody as { error?: unknown }).error === 'string'
      ) {
        return (responseBody as { error: string }).error;
      }
    }
    return 'לא הצלחנו להשלים את הפעולה. אפשר לנסות שוב.';
  }

  get panelIndex(): number {
    if (this.phonePanel === 'search') return 1;
    if (this.phonePanel === 'note') return 2;
    return 0;
  }

  get slideHeight(): number | null {
    if (!this.phoneTabsVisible() || this.panelHeight <= 0) return null;
    return this.panelHeight;
  }

  showPanel(panel: PhonePanel): void {
    this.panelDragging = false;
    this.panelDrag = 0;
    this.touchStartTime = 0;
    this.touchAxis = null;
    if (this.phonePanel !== panel) this.phonePanel = panel;
    this.syncPanelHeight();
  }

  panelHidden(panel: PhonePanel): boolean {
    if (!this.phoneTabsVisible()) return false;
    if (panel === this.phonePanel) return false;
    return panel !== this.dragNeighbor();
  }

  onPanelTouchStart(event: TouchEvent): void {
    if (!this.phoneTabsVisible() || event.touches.length !== 1) return;
    if (this.locationSettingsOpen || this.picker || this.imagePopupProductId != null) return;
    const target = event.target as HTMLElement | null;
    if (target?.closest('input, textarea, select, .note-remove, .cart-remove')) return;
    const touch = event.touches[0];
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
    this.touchStartTime = event.timeStamp;
    this.touchAxis = null;
    this.panelDrag = 0;
  }

  onPanelTouchMove(event: TouchEvent): void {
    if (this.touchStartTime === 0) return;
    if (event.touches.length !== 1) {
      this.finishPanelTouch(0, 0, false);
      return;
    }
    const touch = event.touches[0];
    const dx = touch.clientX - this.touchStartX;
    const dy = touch.clientY - this.touchStartY;
    if (this.touchAxis == null) {
      if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
      this.touchAxis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      if (this.touchAxis === 'y') return;
      this.panelDragging = true;
    }
    if (this.touchAxis !== 'x') return;
    this.panelDrag = this.resistDrag(dx);
    this.syncPanelHeight();
  }

  onPanelTouchEnd(event: TouchEvent): void {
    if (this.touchStartTime === 0) return;
    const touch = event.changedTouches[0];
    const dx = (touch?.clientX ?? this.touchStartX) - this.touchStartX;
    const elapsed = Math.max(event.timeStamp - this.touchStartTime, 1);
    this.finishPanelTouch(dx, dx / elapsed, this.touchAxis === 'x');
  }

  @HostListener('window:resize')
  onStoreListResize(): void {
    this.syncPageFill();
    this.syncPanelHeight();
  }

  private finishPanelTouch(dx: number, velocity: number, horizontal: boolean): void {
    this.touchStartTime = 0;
    this.touchAxis = null;
    if (!horizontal) {
      this.panelDragging = false;
      this.panelDrag = 0;
      return;
    }
    const flick = Math.abs(dx) > 36 && Math.abs(velocity) > 0.8;
    const passed = Math.abs(dx) > 64 || flick;
    const start = this.panelIndex;
    let next = start;
    if (passed && dx < 0 && next < 2) next += 1;
    else if (passed && dx > 0 && next > 0) next -= 1;
    if (next !== start) this.swallowNextClick();
    this.panelDragging = false;
    this.panelDrag = 0;
    this.phonePanel = this.panelAt(next);
    this.syncPanelHeight();
  }

  private resistDrag(dx: number): number {
    const index = this.panelIndex;
    if ((index === 0 && dx > 0) || (index === 2 && dx < 0)) {
      return Math.max(-72, Math.min(72, dx * 0.28));
    }
    return dx;
  }

  private dragNeighbor(): PhonePanel | null {
    if (!this.panelDragging || this.panelDrag === 0) return null;
    if (this.panelDrag < 0 && this.panelIndex < 2) return this.panelAt(this.panelIndex + 1);
    if (this.panelDrag > 0 && this.panelIndex > 0) return this.panelAt(this.panelIndex - 1);
    return null;
  }

  private panelAt(index: number): PhonePanel {
    if (index === 1) return 'search';
    if (index === 2) return 'note';
    return 'cart';
  }

  private swallowNextClick(): void {
    const clip = this.host.nativeElement;
    if (!clip) return;
    const stop = (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
      clip.removeEventListener('click', stop, true);
    };
    clip.addEventListener('click', stop, true);
  }

  private syncPageFill(): void {
    const host = this.host.nativeElement;
    if (!this.phoneTabsVisible()) {
      host.style.removeProperty('--store-list-top');
      return;
    }
    host.style.setProperty('--store-list-top', `${host.offsetTop}px`);
  }

  private syncPanelHeight(): void {
    if (!this.phoneTabsVisible()) {
      this.panelHeight = 0;
      return;
    }
    const current = this.panelElement(this.panelIndex)?.offsetHeight ?? 0;
    const neighborIndex = this.neighborIndex();
    const neighbor = neighborIndex == null ? 0 : this.panelElement(neighborIndex)?.offsetHeight ?? 0;
    const next = Math.max(current, neighbor);
    if (next > 0 && Math.abs(next - this.panelHeight) > 1) this.panelHeight = next;
  }

  private neighborIndex(): number | null {
    const neighbor = this.dragNeighbor();
    if (neighbor == null) return null;
    return neighbor === 'search' ? 1 : neighbor === 'note' ? 2 : 0;
  }

  private panelElement(index: number): HTMLElement | null {
    const ref = index === 1 ? this.searchPanel : index === 2 ? this.notePanel : this.cartPanel;
    return ref?.nativeElement ?? null;
  }

  private bindPhoneTabOffset(): void {
    const viewport = window.visualViewport;
    if (!viewport) return;
    const host = this.host.nativeElement;
    const update = () => {
      const covered = Math.max(0, Math.round(window.innerHeight - viewport.offsetTop - viewport.height));
      host.style.setProperty('--phone-tabs-bottom', `${covered}px`);
    };
    viewport.addEventListener('resize', update);
    viewport.addEventListener('scroll', update);
    update();
    this.releasePhoneTabOffset = () => {
      viewport.removeEventListener('resize', update);
      viewport.removeEventListener('scroll', update);
    };
  }

  private phoneTabsVisible(): boolean {
    return typeof window !== 'undefined' && window.matchMedia('(max-width: 860px)').matches;
  }

  private refreshCameraAvailable(): void {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      this.cameraAvailable = false;
      return;
    }
    const hasCameraApi = window.isSecureContext && !!navigator.mediaDevices?.getUserMedia;
    const phoneLike =
      window.matchMedia('(pointer: coarse)').matches ||
      window.matchMedia('(hover: none)').matches ||
      window.matchMedia('(max-width: 768px)').matches;
    this.cameraAvailable = hasCameraApi && phoneLike;
    if (!hasCameraApi || !navigator.mediaDevices.enumerateDevices) return;
    void navigator.mediaDevices.enumerateDevices().then((devices) => {
      const hasVideo = devices.some((device) => device.kind === 'videoinput');
      if (devices.length > 0 && !hasVideo) this.cameraAvailable = false;
    }).catch(() => {});
  }
}
